import { Button, Group, Modal, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DoubleConfirmReasonDialogProps {
  opened: boolean;
  expected: string;
  title?: string;
  message?: string;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  reasonRequiredText?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * Callback invoked once the user typed the matching name and a reason.
   * Receives the typed reason verbatim.
   */
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

/**
 * Like DoubleConfirmDialog but also captures a free-form reason that is
 * passed back to the caller. Used for backend deletes that require an audit
 * reason (e.g. Träger, Plan).
 */
export function DoubleConfirmReasonDialog({
  opened,
  expected,
  title,
  message,
  reasonLabel,
  reasonPlaceholder,
  reasonRequiredText,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: DoubleConfirmReasonDialogProps) {
  const { t } = useTranslation('common');
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const matches = name.trim() === expected.trim();
  const reasonOk = reason.trim().length >= 3;

  const handleClose = () => {
    setName('');
    setReason('');
    onCancel();
  };

  const handleConfirm = () => {
    if (!matches || !reasonOk) return;
    const r = reason;
    setName('');
    setReason('');
    onConfirm(r);
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={title ?? t('confirm.delete.title')} centered>
      <Stack>
        {message ? <Text>{message}</Text> : null}
        <Text fw={600}>{expected}</Text>
        <TextInput
          label={t('confirm.delete.placeholder')}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          error={name && !matches ? t('confirm.delete.error') : null}
          autoFocus
          aria-required="true"
        />
        <Textarea
          label={reasonLabel ?? t('confirm.delete.placeholder')}
          placeholder={reasonPlaceholder}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          autosize
          minRows={2}
          error={reason && !reasonOk ? reasonRequiredText ?? null : null}
          aria-required="true"
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose}>
            {cancelLabel ?? t('actions.cancel')}
          </Button>
          <Button color="red" disabled={!matches || !reasonOk} onClick={handleConfirm}>
            {confirmLabel ?? t('actions.delete')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
